import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SearchIcon from '@material-ui/icons/Search';
import 'leaflet/dist/leaflet.css';
import { useSelector } from 'react-redux';
import useVehicleTracking from 'src/app/hooks/useVehicleTracking';
import useProgressiveVehicleTracking from 'src/app/hooks/useProgressiveVehicleTracking';
import { _convertObjectToQuery } from 'src/app/utils/apiService';
import axiosInterceptor from 'src/app/axiosInterceptor';
import './style.css';
import CameraVehicleTable from './components/CameraVehicleTable';
import CameraWorkspace from './components/CameraWorkspace';
import CMSMapWidget from './components/CMSMapWidget';
import {
  buildStreamKey,
  CAMERA_DEVICE_TYPES,
  getDeviceTypeName,
  getVehicleLabel,
  GRID_OPTIONS
} from './helpers';

const USER_SEARCH_MIN_CHARS = 2;
const USER_SEARCH_DEBOUNCE_MS = 400;
const USER_SEARCH_LIMIT = 50;
const CAMERA_DEVICE_TYPE_IDS = [7, 8];

const getEndUserOptionLabel = option =>
  [option?.end_user_name, option?.end_user_phone].filter(Boolean).join(' - ');

// Kết quả search là danh sách thiết bị — gom lại thành danh sách khách hàng cuối không trùng
const toEndUserOptions = devices => {
  const map = new Map();
  devices.forEach(device => {
    if (!device?.end_user_id || map.has(device.end_user_id)) return;
    map.set(device.end_user_id, {
      end_user_id: device.end_user_id,
      end_user_name: device.end_user_name,
      end_user_phone: device.end_user_phone,
      agency_name: device.agency_name
    });
  });
  return Array.from(map.values());
};

const CMSCameras = () => {
  const workspaceRef = useRef(null);
  const sidebarRef = useRef(null);
  const { isUser, isAdmin, userId, agencyId, adminId } = useSelector(
    state => state.authSlice
  );
  const { listVehicleTracking } = useSelector(state => state.vehicleSlice);
  const trackingId = adminId ?? agencyId;

  useVehicleTracking();
  const { load: loadVehiclesProgressively, progress: loadProgress } =
    useProgressiveVehicleTracking();

  const [searchValue, setSearchValue] = useState('');
  const [gridSize, setGridSize] = useState(16);
  const [focusedSlot, setFocusedSlot] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [slots, setSlots] = useState(() => Array(16).fill(null));
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [tableHeight, setTableHeight] = useState(54);
  const [draggedSlotIndex, setDraggedSlotIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);
  const [userKeyword, setUserKeyword] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedEndUser, setSelectedEndUser] = useState(null);
  const selectedEndUserId = selectedEndUser?.end_user_id;

  // Nạp dần theo trang (300 xe/trang): trang 1 hiển thị ngay, các trang sau nối dần ở nền
  useEffect(() => {
    let basePath;
    let query = { device_type_id: CAMERA_DEVICE_TYPE_IDS };
    if (isUser) {
      if (!userId) return;
      basePath = '/vehicles/tracking/me';
      query = { ...query, agency_id: agencyId || undefined };
    } else if (selectedEndUserId) {
      basePath = `/vehicles/tracking/endUser/${selectedEndUserId}`;
    } else {
      if (!trackingId) return;
      basePath = `/vehicles/tracking/agency/${trackingId}`;
    }

    loadVehiclesProgressively(
      (page, pageSize) =>
        `${basePath}?${_convertObjectToQuery({ ...query, page, page_size: pageSize })}`
    );
  }, [
    agencyId,
    isUser,
    loadVehiclesProgressively,
    selectedEndUserId,
    trackingId,
    userId
  ]);

  useEffect(() => {
    const keyword = userKeyword.trim();
    if (
      !isAdmin ||
      keyword.length < USER_SEARCH_MIN_CHARS ||
      keyword === getEndUserOptionLabel(selectedEndUser)
    ) {
      setUserOptions(selectedEndUser ? [selectedEndUser] : []);
      setUserLoading(false);
      return undefined;
    }

    let cancelled = false;
    setUserLoading(true);
    const timer = setTimeout(async () => {
      try {
        // Chỉ lấy thiết bị camera để không gợi ý khách hàng không có camera nào
        const res = await axiosInterceptor.get(
          `/vehicles/tracking/search?${_convertObjectToQuery({
            keywords: keyword,
            limit: USER_SEARCH_LIMIT,
            device_type_id: CAMERA_DEVICE_TYPE_IDS
          })}`
        );
        if (cancelled) return;
        setUserOptions(toEndUserOptions(res?.data?.payload?.devices || []));
      } catch (error) {
        if (!cancelled) setUserOptions([]);
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    }, USER_SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isAdmin, selectedEndUser, userKeyword]);

  useEffect(() => {
    setSlots(prev => {
      const next = prev.slice(0, gridSize);
      while (next.length < gridSize) {
        next.push(null);
      }
      return next;
    });
    setFocusedSlot(prev => Math.min(prev, gridSize - 1));
  }, [gridSize]);

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === workspaceRef.current);
    };

    document.addEventListener('fullscreenchange', syncFullscreenState);
    return () => document.removeEventListener('fullscreenchange', syncFullscreenState);
  }, []);

  const cameraVehicles = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();

    return (Array.isArray(listVehicleTracking) ? listVehicleTracking : [])
      .filter(vehicle => CAMERA_DEVICE_TYPES.has(getDeviceTypeName(vehicle)))
      .filter(vehicle => {
        if (!keyword) return true;
        const haystack = [
          vehicle?.license_plate,
          vehicle?.name,
          vehicle?.device_serial
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(keyword);
      })
      .sort((first, second) =>
        getVehicleLabel(first).localeCompare(getVehicleLabel(second), 'vi')
      );
  }, [listVehicleTracking, searchValue]);

  useEffect(() => {
    if (!cameraVehicles.length) {
      setSelectedVehicleId(null);
      return;
    }
    if (
      selectedVehicleId !== null &&
      !cameraVehicles.some(vehicle => vehicle.device_id === selectedVehicleId)
    ) {
      setSelectedVehicleId(null);
    }
  }, [cameraVehicles, selectedVehicleId]);

  const vehicleMapBySerial = useMemo(() => {
    const map = new Map();

    cameraVehicles.forEach(vehicle => {
      if (vehicle?.device_serial) {
        map.set(vehicle.device_serial, vehicle);
      }
    });

    return map;
  }, [cameraVehicles]);

  const renderedSlots = useMemo(
    () =>
      slots.map(slot => {
        if (!slot) return null;

        const vehicle = vehicleMapBySerial.get(slot.deviceSerial) || slot.vehicle;
        return {
          ...slot,
          vehicle,
          label: `${getVehicleLabel(vehicle)} - Kênh ${slot.channel}`
        };
      }),
    [slots, vehicleMapBySerial]
  );

  const selectedVehicle = useMemo(
    () =>
      cameraVehicles.find(vehicle => vehicle.device_id === selectedVehicleId) ||
      null,
    [cameraVehicles, selectedVehicleId]
  );

  const gridColumns = useMemo(() => {
    const option = GRID_OPTIONS.find(item => item.size === gridSize);
    return option?.columns || 4;
  }, [gridSize]);

  const findStreamIndex = useCallback(
    streamKey => slots.findIndex(slot => slot?.key === streamKey),
    [slots]
  );

  const toggleStream = useCallback(
    (vehicle, channel) => {
      const key = buildStreamKey(vehicle, channel);
      const stream = {
        key,
        channel,
        deviceSerial: vehicle.device_serial,
        label: `${getVehicleLabel(vehicle)} - Kênh ${channel}`,
        vehicle
      };
      let nextFocusedSlot = focusedSlot;

      setSlots(prev => {
        const existingIndex = prev.findIndex(slot => slot?.key === key);
        if (existingIndex !== -1) {
          const next = [...prev];
          next[existingIndex] = null;
          nextFocusedSlot = existingIndex;
          return next;
        }

        const next = [...prev];
        const focusIndex =
          focusedSlot < next.length && next[focusedSlot] === null
            ? focusedSlot
            : next.findIndex(slot => slot === null);
        const targetIndex = focusIndex !== -1 ? focusIndex : focusedSlot;

        next[targetIndex] = stream;
        nextFocusedSlot = Math.min(targetIndex + 1, gridSize - 1);
        return next;
      });

      setFocusedSlot(nextFocusedSlot);
    },
    [focusedSlot, gridSize]
  );

  const handleSelectVehicle = useCallback(vehicle => {
    if (vehicle?.device_id) {
      setSelectedVehicleId(vehicle.device_id);
    }
  }, []);

  const handleTileDragStart = useCallback((event, index) => {
    if (!slots[index]) return;

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
    setDraggedSlotIndex(index);
    setDropTargetIndex(index);
  }, [slots]);

  const handleTileDragOver = useCallback((event, index) => {
    if (draggedSlotIndex === null) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dropTargetIndex !== index) {
      setDropTargetIndex(index);
    }
  }, [draggedSlotIndex, dropTargetIndex]);

  const handleTileDragLeave = useCallback((event, index) => {
    if (dropTargetIndex !== index) return;

    const nextTarget = event.relatedTarget;
    if (event.currentTarget.contains(nextTarget)) return;
    setDropTargetIndex(null);
  }, [dropTargetIndex]);

  const resetDragState = useCallback(() => {
    setDraggedSlotIndex(null);
    setDropTargetIndex(null);
  }, []);

  const handleTileDrop = useCallback((event, targetIndex) => {
    event.preventDefault();

    const rawSourceIndex = event.dataTransfer.getData('text/plain');
    const sourceIndex =
      rawSourceIndex !== '' ? Number(rawSourceIndex) : draggedSlotIndex;

    if (!Number.isInteger(sourceIndex) || sourceIndex < 0 || sourceIndex >= slots.length) {
      resetDragState();
      return;
    }

    if (sourceIndex === targetIndex || !slots[sourceIndex]) {
      setFocusedSlot(targetIndex);
      resetDragState();
      return;
    }

    setSlots(prev => {
      const next = [...prev];
      const sourceSlot = next[sourceIndex];
      next[sourceIndex] = next[targetIndex];
      next[targetIndex] = sourceSlot;
      return next;
    });
    setFocusedSlot(targetIndex);
    resetDragState();
  }, [draggedSlotIndex, resetDragState, slots]);

  const handleResizeStart = useCallback(
    event => {
      event.preventDefault();
      const sidebarEl = sidebarRef.current;
      if (!sidebarEl) return;

      const sidebarRect = sidebarEl.getBoundingClientRect();

      const onMouseMove = moveEvent => {
        const rawHeight = moveEvent.clientY - sidebarRect.top;
        const nextRatio = (rawHeight / sidebarRect.height) * 100;
        const boundedRatio = Math.max(28, Math.min(76, nextRatio));
        setTableHeight(boundedRatio);
      };

      const onMouseUp = () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    []
  );

  const toggleFullscreen = useCallback(async () => {
    const workspaceEl = workspaceRef.current;
    if (!workspaceEl) return;

    if (document.fullscreenElement === workspaceEl) {
      await document.exitFullscreen?.();
      return;
    }

    await workspaceEl.requestFullscreen?.();
  }, []);

  const activeStreamCount = renderedSlots.filter(Boolean).length;

  const gridRef = useRef(null);
  const [tileMaxWidth, setTileMaxWidth] = useState(null);

  useEffect(() => {
    const calculateTileWidth = () => {
      const gridEl = gridRef.current;
      if (!gridEl) return;

      const gridHeight = gridEl.clientHeight;
      const rows = gridColumns;
      const totalGap = (rows - 1) * 3; // gap: 3px
      const availableHeight = gridHeight - totalGap;
      const tileHeight = availableHeight / rows;
      const maxWidth = Math.floor(tileHeight * (16 / 9));

      setTileMaxWidth(maxWidth > 0 ? maxWidth : null);
    };

    calculateTileWidth();

    const resizeObserver = new ResizeObserver(calculateTileWidth);
    if (gridRef.current) {
      resizeObserver.observe(gridRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [gridColumns]);

  return (
    <Box className="cms-camera-page">
      <Box className="cms-camera-sidebar">
        <Box className="cms-camera-sidebar-header">
          <Typography variant="h5">GIÁM SÁT CAMERA</Typography>
          <Typography variant="body2">
            Danh sách thiết bị: ({cameraVehicles.length}/{cameraVehicles.length})
          </Typography>
          {loadProgress.loadingMore && (
            <Typography variant="caption" color="textSecondary">
              Đang tải thêm thiết bị… {loadProgress.loaded}/{loadProgress.total}
            </Typography>
          )}
        </Box>

        <Box className="cms-camera-sidebar-body" ref={sidebarRef}>
          <Box
            className="cms-camera-sidebar-top"
            style={{ height: `calc(${tableHeight}% - 6px)` }}
          >
            {/* Chỉ admin: check dương isAdmin để khi F5 (auth chưa nạp xong) không nháy ô này */}
            {isAdmin && (
              <Autocomplete
                size="small"
                className="cms-camera-search"
                options={userOptions}
                value={selectedEndUser}
                loading={userLoading}
                filterOptions={options => options}
                getOptionLabel={getEndUserOptionLabel}
                getOptionSelected={(option, value) =>
                  option.end_user_id === value.end_user_id
                }
                noOptionsText={
                  userKeyword.trim().length < USER_SEARCH_MIN_CHARS
                    ? 'Nhập tên hoặc SĐT khách hàng'
                    : 'Không tìm thấy khách hàng'
                }
                loadingText="Đang tìm..."
                onInputChange={(event, value) => setUserKeyword(value)}
                onChange={(event, value) => {
                  setSelectedEndUser(value);
                  setSearchValue('');
                }}
                renderOption={option => (
                  <Box>
                    <Typography variant="body2">
                      {getEndUserOptionLabel(option)}
                    </Typography>
                    {option.agency_name && (
                      <Typography variant="caption" color="textSecondary">
                        {option.agency_name}
                      </Typography>
                    )}
                  </Box>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Chọn khách hàng (tên/SĐT)..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userLoading && <CircularProgress size={14} />}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            )}

            <TextField
              fullWidth={true}
              size="small"
              variant="outlined"
              value={searchValue}
              onChange={event => setSearchValue(event.target.value)}
              placeholder={
                selectedEndUser
                  ? `Tìm biển số xe của ${selectedEndUser.end_user_name || 'khách hàng'}...`
                  : 'Tìm biển số xe...'
              }
              className="cms-camera-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />

            <CameraVehicleTable
              vehicles={cameraVehicles}
              isLoading={loadProgress.loading}
              selectedVehicleId={selectedVehicleId}
              onSelectVehicle={handleSelectVehicle}
              findStreamIndex={findStreamIndex}
              toggleStream={toggleStream}
            />
          </Box>

          <button
            type="button"
            className="cms-camera-sidebar-divider"
            onMouseDown={handleResizeStart}
            aria-label="Kéo thay đổi chiều cao bản đồ"
          >
            <span />
          </button>

          <Box className="cms-camera-sidebar-bottom">
            <CMSMapWidget
              vehicles={cameraVehicles}
              selectedVehicle={selectedVehicle}
              mapHeight={tableHeight}
            />
          </Box>
        </Box>
      </Box>

      <CameraWorkspace
        workspaceRef={workspaceRef}
        gridRef={gridRef}
        tileMaxWidth={tileMaxWidth}
        gridColumns={gridColumns}
        gridSize={gridSize}
        gridOptions={GRID_OPTIONS}
        activeStreamCount={activeStreamCount}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
        renderedSlots={renderedSlots}
        focusedSlot={focusedSlot}
        dropTargetIndex={dropTargetIndex}
        onGridSizeChange={setGridSize}
        onFocusSlot={setFocusedSlot}
        onTileDragStart={handleTileDragStart}
        onTileDragOver={handleTileDragOver}
        onTileDragLeave={handleTileDragLeave}
        onTileDrop={handleTileDrop}
        onTileDragEnd={resetDragState}
      />
    </Box>
  );
};

export default CMSCameras;
